import type { ReactNode } from 'react';
import { CollapsibleRows } from './CollapsibleRows';
import { ProposalShell } from './ProposalShell';

interface Props<TRow> {
  title: string;
  description: string;
  proposalId: string;
  rows: readonly TRow[];
  /** Stable identity per row — the id or code the row is keyed by. */
  rowKey: (row: TRow) => string;
  /** Leading cell: what the row is. */
  primary: (row: TRow) => ReactNode;
  /** Trailing cell: the detail that makes the row reviewable (amount, city, role). */
  secondary?: (row: TRow) => ReactNode;
  /** Rendered under the list — per-currency totals and the like. */
  footer?: ReactNode;
  resolved?: string | undefined;
  onConfirm: (proposalId: string) => void;
  onCancel: (proposalId: string) => void;
}

/**
 * Confirmation card for any bulk proposal: every row the action will touch is listed,
 * because a count alone would ask the user to confirm work they cannot see.
 */
export function BulkProposalCard<TRow>({
  title,
  description,
  proposalId,
  rows,
  rowKey,
  primary,
  secondary,
  footer,
  resolved,
  onConfirm,
  onCancel
}: Props<TRow>) {
  return (
    <ProposalShell
      title={title}
      description={description}
      proposalId={proposalId}
      resolved={resolved}
      onConfirm={onConfirm}
      onCancel={onCancel}
    >
      <CollapsibleRows
        rows={rows}
        // Scrolls rather than grows: an expanded long list must not push the chat away.
        container={children => (
          <ul className='flex flex-col gap-2xs m-0 p-0 list-none max-h-64 overflow-y-auto'>
            {children}
          </ul>
        )}
        renderRow={row => (
          <li
            key={rowKey(row)}
            className='flex items-baseline justify-between gap-sm border-b border-border-base py-2xs last:border-b-0'
          >
            <span className='text-foreground-base text-sm'>{primary(row)}</span>
            {secondary && (
              <span className='text-foreground-base text-sm whitespace-nowrap'>
                {secondary(row)}
              </span>
            )}
          </li>
        )}
      />
      {footer}
    </ProposalShell>
  );
}
